import React, { useId } from "react";

/**
 * A text field that offers the values already in use while still accepting a value of
 * the user's own.
 *
 * The SCO onboarding form needs both behaviours per field (PBI #292): office and country
 * are limited to the company's existing values, while a job title must be creatable. Use
 * this component for the fields that allow their own value and the constrained `Dropdown`
 * for the ones that do not.
 *
 * @param {object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {(value: string) => void} props.onChange - Called with the new value
 * @param {Array<string | {value: string, label?: string}>} [props.options] - Values offered
 *   as suggestions; an object shows its label beside the value, and picking it stores the value
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.disabled] - Disables the field
 */
const SuggestInputField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
}) => {
  const fieldId = useId();
  const suggestions = options
    .map((option) =>
      typeof option === "string" ? { value: option } : option
    )
    .filter(
      (option) =>
        typeof option?.value === "string" && option.value.trim() !== ""
    );

  return (
    <div className="w-full">
      <label
        htmlFor={fieldId}
        className="block text-sm font-bold text-gray-800 mb-2"
      >
        {label}
      </label>
      <input
        id={fieldId}
        type="text"
        list={`${fieldId}-options`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full ${
          disabled
            ? "bg-gray-100 cursor-not-allowed opacity-60 border border-gray-300 rounded-md px-2 py-2"
            : "input-default"
        }`}
      />
      <datalist id={`${fieldId}-options`}>
        {suggestions.map((option) => (
          <option
            key={option.value}
            value={option.value}
            label={option.label}
          />
        ))}
      </datalist>
    </div>
  );
};

export default SuggestInputField;
